import React, { Component } from "react";
import slideShowService from "../../services/slideshowService";
import {
  IReturnScenariosCartApplicationState,
  IReturnScenariosWorkflowAvailability,
  IReturnScenariosWorkflowSelections,
  ReturnScenariosSelectionKey,
} from "../types/states";
import ReturnScenariosWorkflow from "../workflow/returnScenariosWorkflow.component";

const getDefaultSelections = (): IReturnScenariosWorkflowSelections => ({
  section2: true,
  section3: true,
  historical: false,
});

const getDefaultAvailability = (): IReturnScenariosWorkflowAvailability => ({
  section2: true,
  section3: true,
  historical: false,
});

const PHONE_VALIDATION_MESSAGE = 'Enter a valid phone number.';

class ReturnScenariosCartApplication extends Component<any, IReturnScenariosCartApplicationState> {
  constructor(props: null) {
    super(props);
    this.state = {
      workflowOpen: false,
      workflowSubmitting: false,
      workflowEntryPoint: undefined,
      workflowSelections: getDefaultSelections(),
      workflowAvailability: getDefaultAvailability(),
      workflowUserInfo: this.getEmptyUserInfo(),
      workflowErrors: {},
      workflowSession: null,
      workflowPdfUrl: undefined,
      workflowErrorMessage: undefined,
    };
  }

  componentDidMount() {
    window.addEventListener('ssa-return-scenarios:open-workflow', this.handleOpenWorkflow as EventListener);
    document.addEventListener('ssa-return-scenarios:open-workflow', this.handleOpenWorkflow as EventListener);
    document.addEventListener('keydown', this.handleGlobalKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('ssa-return-scenarios:open-workflow', this.handleOpenWorkflow as EventListener);
    document.removeEventListener('ssa-return-scenarios:open-workflow', this.handleOpenWorkflow as EventListener);
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
  }

  focusActiveSection = () => {
    const controller: any = slideShowService.currentChapterController;
    if (controller && typeof controller.focusActiveSection === 'function') {
      controller.focusActiveSection();
      return;
    }
    const slideshow: any = slideShowService.slideshow;
    if (!slideshow || typeof slideshow._focusDefaultElement !== 'function') {
      return;
    }
    const activeSection = slideshow._activeSectionController;
    if (!activeSection) {
      return;
    }
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        slideshow._focusDefaultElement(activeSection, 0);
      });
    } else {
      slideshow._focusDefaultElement(activeSection, 0);
    }
  };

  render() {
    return (
      <div className="return-scenarios-cart-app">
        {this.renderWorkflow()}
      </div>
    );
  }

  handleOpenWorkflow = (event: Event) => {
    const detail: any = (event as CustomEvent).detail || {};
    const selections = detail.selections || {};
    const rawSession = detail.session || null;
    const session = rawSession ? { ...rawSession, pdfUrl: undefined, viewed: false, emailed: false } : null;
    const entryPoint: 'single' | 'portfolio' = detail.entryPoint === 'portfolio' ? 'portfolio' : 'single';
    const availability = this.getWorkflowAvailability(detail);
    const normalizedSelections = this.normalizeWorkflowSelections(entryPoint, availability, selections, session);
    const sourceUser = session?.userInfo || {};
    const designationsFromSession = Array.isArray(sourceUser.designations)
      ? sourceUser.designations.slice(0)
      : Array.isArray(sourceUser.advisorDesignations)
        ? sourceUser.advisorDesignations.slice(0)
        : [];
    const otherDesignationsFromSession =
      sourceUser.otherDesignations !== undefined && sourceUser.otherDesignations !== null
        ? sourceUser.otherDesignations
        : (sourceUser.advisorCustomDesignation || '');
    const userInfo = session && session.userInfo ? {
      clientName: sourceUser.clientName || '',
      advisorName: sourceUser.advisorName || '',
      advisorPhone: sourceUser.advisorPhone || '',
      designations: designationsFromSession,
      otherDesignations: otherDesignationsFromSession,
      advisorDesignations: designationsFromSession.slice(0),
      advisorCustomDesignation: otherDesignationsFromSession,
    } : this.getEmptyUserInfo();
    if (session && session.userInfo) {
      session.userInfo = {
        ...session.userInfo,
        advisorDesignations: designationsFromSession.slice(0),
        advisorCustomDesignation: otherDesignationsFromSession,
      };
    }

    this.setState({
      workflowOpen: true,
      workflowSubmitting: false,
      workflowEntryPoint: entryPoint,
      workflowSelections: normalizedSelections,
      workflowAvailability: availability,
      workflowUserInfo: userInfo,
      workflowErrors: {},
      workflowSession: session,
      workflowPdfUrl: undefined,
      workflowErrorMessage: undefined,
    }, () => {
      this.persistWorkflowSelections();
      this.persistWorkflowUserInfo();
    });
  };

  getEmptyUserInfo = () => ({
    clientName: '',
    advisorName: '',
    advisorPhone: '',
    designations: [],
    otherDesignations: '',
    advisorDesignations: [],
    advisorCustomDesignation: '',
  });

  closeWorkflow = () => {
    const controller: any = slideShowService.currentChapterController;
    this.setState({
      workflowOpen: false,
      workflowSubmitting: false,
      workflowErrors: {},
      workflowErrorMessage: undefined,
    }, this.focusActiveSection);
    if (controller && controller.releaseReturnScenariosWorkflowLock) {
      controller.releaseReturnScenariosWorkflowLock();
    }
  };

  handleGlobalKeyDown = (event: KeyboardEvent) => {
    if (!this.state.workflowOpen) {
      return;
    }

    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      event.preventDefault();
      this.closeWorkflow();
    }
  };

  handleUserInputChange = (field: string, value: string) => {
    this.setState((prevState) => {
      const nextInfo = {
        ...prevState.workflowUserInfo,
        [field]: value,
      };
      if (field === 'otherDesignations') {
        nextInfo.advisorCustomDesignation = value;
      }
      if (field === 'advisorCustomDesignation') {
        nextInfo.otherDesignations = value;
      }
      return {
        workflowUserInfo: nextInfo,
      };
    }, this.persistWorkflowUserInfo);
  };

  handleDesignationToggle = (designation: string) => {
    this.setState((prevState) => {
      const set = new Set(prevState.workflowUserInfo.designations);
      if (set.has(designation)) {
        set.delete(designation);
      } else {
        set.add(designation);
      }
      const updatedDesignations = Array.from(set);
      return {
        workflowUserInfo: {
          ...prevState.workflowUserInfo,
          designations: updatedDesignations,
          advisorDesignations: updatedDesignations,
        },
      };
    }, this.persistWorkflowUserInfo);
  };

  handleSelectionToggle = (field: ReturnScenariosSelectionKey) => {
    this.setState((prevState) => {
      const availability = prevState.workflowAvailability;
      const current = prevState.workflowSelections;
      const next: IReturnScenariosWorkflowSelections = {
        section2: current.section2,
        section3: current.section3,
        historical: current.historical,
      };

      if ((field === 'section2' || field === 'section3') && availability[field]) {
        next[field] = !current[field];
      }

      if (field === 'historical') {
        const mainSelected = (availability.section2 && current.section2) || (availability.section3 && current.section3);
        if (availability.historical && mainSelected) {
          next.historical = !current.historical;
        }
      }

      const mainSelected = (availability.section2 && next.section2) || (availability.section3 && next.section3);
      if (!mainSelected || !availability.historical) {
        next.historical = false;
      }

      if (
        next.section2 === current.section2 &&
        next.section3 === current.section3 &&
        next.historical === current.historical
      ) {
        return null;
      }

      return {
        workflowSelections: next,
      };
    }, this.persistWorkflowSelections);
  };

  persistWorkflowUserInfo = () => {
    const controller: any = slideShowService.currentChapterController;
    if (controller && controller.updateReturnScenariosPdfUserInfoDraft) {
      const info = this.state.workflowUserInfo;
      controller.updateReturnScenariosPdfUserInfoDraft({
        clientName: info.clientName || '',
        advisorName: info.advisorName || '',
        advisorPhone: info.advisorPhone || '',
        advisorDesignations: (info.advisorDesignations && info.advisorDesignations.length ? info.advisorDesignations : info.designations) || [],
        advisorCustomDesignation: info.advisorCustomDesignation !== undefined ? info.advisorCustomDesignation : info.otherDesignations || '',
      });
    }
  };

  persistWorkflowSelections = () => {
    const controller: any = slideShowService.currentChapterController;
    if (controller && controller.updateReturnScenariosPdfSelections) {
      controller.updateReturnScenariosPdfSelections(
        this.state.workflowEntryPoint || 'single',
        this.state.workflowSelections
      );
    }
  };

  getWorkflowAvailability = (detail: any): IReturnScenariosWorkflowAvailability => ({
    section2: !!detail.singleSummary,
    section3: !!detail.portfolioSummary,
    historical: detail.historicalAvailable !== false,
  });

  normalizeWorkflowSelections = (
    entryPoint: 'single' | 'portfolio',
    availability: IReturnScenariosWorkflowAvailability,
    selections: Partial<IReturnScenariosWorkflowSelections>,
    session: any
  ): IReturnScenariosWorkflowSelections => {
    const source = (session && session.selections) || selections || {};
    const normalized: IReturnScenariosWorkflowSelections = {
      section2: availability.section2,
      section3: availability.section3,
      historical: false,
    };

    if (entryPoint === 'portfolio' && availability.section3) {
      normalized.section3 = true;
      if (!availability.section2) {
        normalized.section2 = false;
      }
    }

    if (availability.section2) {
      normalized.section2 = source.section2 !== undefined ? !!source.section2 : normalized.section2;
    } else {
      normalized.section2 = false;
    }

    if (availability.section3) {
      normalized.section3 = source.section3 !== undefined ? !!source.section3 : normalized.section3;
    } else {
      normalized.section3 = false;
    }

    const mainSelected = (normalized.section2 && availability.section2) || (normalized.section3 && availability.section3);

    if (availability.historical && mainSelected) {
      normalized.historical = source.historical !== undefined ? !!source.historical : false;
    } else {
      normalized.historical = false;
    }

    if (!mainSelected) {
      if (availability.section2) {
        normalized.section2 = true;
      } else if (availability.section3) {
        normalized.section3 = true;
      }
    }

    if (!availability.historical || !(normalized.section2 || normalized.section3)) {
      normalized.historical = false;
    }

    return normalized;
  };

  validateWorkflowUserInfo = () => {
    const info = this.state.workflowUserInfo;
    const errors: { [key: string]: string } = {};

    if (info.advisorPhone && info.advisorPhone.trim()) {
      const digits = info.advisorPhone.replace(/\D/g, '');
      if (digits.length !== 10) {
        errors.advisorPhone = PHONE_VALIDATION_MESSAGE;
      }
    }

    return errors;
  };

  submitWorkflow = () => {
    if (this.state.workflowSubmitting) {
      return;
    }

    const errors = this.validateWorkflowUserInfo();
    if (Object.keys(errors).length > 0) {
      this.setState({ workflowErrors: errors, workflowErrorMessage: this.extractFirstError(errors) });
      return;
    }

    const controller: any = slideShowService.currentChapterController;
    if (!controller || !controller.createReturnScenariosPdf) {
      this.setState({ workflowErrorMessage: 'PDF workflow is unavailable right now.' });
      return;
    }

    const payload = {
      entryPoint: this.state.workflowEntryPoint || 'single',
      selections: this.state.workflowSelections,
      userInfo: this.state.workflowUserInfo,
    };

    this.setState({ workflowSubmitting: true, workflowErrorMessage: undefined, workflowErrors: {} });

    const request: any = controller.createReturnScenariosPdf(payload);
    if (!request || !request.then) {
      this.setState({ workflowSubmitting: false, workflowErrorMessage: 'We could not create your PDF. Please try again.' });
      return;
    }

    request.then(
      (session: any) => {
        this.setState({
          workflowSubmitting: false,
          workflowSession: session,
          workflowPdfUrl: session && session.pdfUrl ? session.pdfUrl : undefined,
          workflowErrorMessage: undefined,
        });
      },
      (message: any) => {
        this.setState({
          workflowSubmitting: false,
          workflowErrorMessage: typeof message === 'string' ? message : 'We could not create your PDF. Please try again.',
        });
      }
    );
  };

  extractFirstError = (errors: { [key: string]: string }) => {
    if (!errors) { return undefined; }
    if (errors.clientName) { return errors.clientName; }
    if (errors.advisorName) { return errors.advisorName; }
    if (errors.advisorPhone) { return errors.advisorPhone; }
    return undefined;
  };

  handleViewPdf = () => {
    const controller: any = slideShowService.currentChapterController;
    if (!controller || !controller.markReturnScenariosPdfViewed) {
      return;
    }
    const url = controller.markReturnScenariosPdfViewed();
    if (url) {
      window.open(url, '_blank');
    }
    const session = controller.getReturnScenariosPdfSession ? controller.getReturnScenariosPdfSession() : this.state.workflowSession;
    this.setState({ workflowSession: session, workflowPdfUrl: session && session.pdfUrl ? session.pdfUrl : this.state.workflowPdfUrl });
  };

  handleEmailPdf = () => {
    const controller: any = slideShowService.currentChapterController;
    if (!controller || !controller.markReturnScenariosPdfEmailed) {
      return;
    }
    const url = controller.markReturnScenariosPdfEmailed();
    if (url) {
      const subject = encodeURIComponent('Custom Structured Solutions 2 Rate Sheet');
      const body = encodeURIComponent('Here is your report:\n' + url);
      window.location.href = 'mailto:?subject=' + subject + '&body=' + body;
    }
    const session = controller.getReturnScenariosPdfSession ? controller.getReturnScenariosPdfSession() : this.state.workflowSession;
    this.setState({ workflowSession: session, workflowPdfUrl: session && session.pdfUrl ? session.pdfUrl : this.state.workflowPdfUrl });
  };

  handleResetWorkflow = () => {
    const controller: any = slideShowService.currentChapterController;
    const defaultSelections = getDefaultSelections();
    const defaultAvailability = getDefaultAvailability();
    const defaultUserInfo = this.getEmptyUserInfo();

    if (controller && controller.resetReturnScenariosPdfSession) {
      controller.resetReturnScenariosPdfSession();
    }
    this.setState({
      workflowOpen: false,
      workflowSession: null,
      workflowPdfUrl: undefined,
      workflowUserInfo: defaultUserInfo,
      workflowSelections: defaultSelections,
      workflowAvailability: defaultAvailability,
      workflowEntryPoint: 'single',
      workflowErrors: {},
      workflowErrorMessage: undefined,
      workflowSubmitting: false,
    }, () => {
      if (controller && controller.resetFormState) {
        controller.resetFormState();
      }
      if (controller && controller.releaseReturnScenariosWorkflowLock) {
        controller.releaseReturnScenariosWorkflowLock();
      }
      if (controller && controller.navigateToReturnScenariosSection) {
        controller.navigateToReturnScenariosSection('section1', 'step1');
      }
      this.focusActiveSection();
    });
  };

  renderWorkflow = () => {
    if (!this.state.workflowOpen) {
      return null;
    }

    const hasPdf = !!this.state.workflowPdfUrl;
    const info = this.state.workflowUserInfo;
    const errors = this.state.workflowErrors;
    return (
      <ReturnScenariosWorkflow
        open={this.state.workflowOpen}
        hasPdf={hasPdf}
        userInfo={info}
        selections={this.state.workflowSelections}
        availability={this.state.workflowAvailability}
        errors={errors}
        errorMessage={this.state.workflowErrorMessage}
        submitting={this.state.workflowSubmitting}
        onClose={this.closeWorkflow}
        onSubmit={this.submitWorkflow}
        onReset={this.handleResetWorkflow}
        onUserInfoChange={this.handleUserInputChange}
        onDesignationToggle={this.handleDesignationToggle}
        onSelectionToggle={this.handleSelectionToggle}
        onViewPdf={this.handleViewPdf}
        onEmailPdf={this.handleEmailPdf}
      />
    );
  };
}

export default ReturnScenariosCartApplication;
